import React, { useState, useEffect } from 'react';
import { useOrderSelection } from '@/hooks/useOrderSelection';
import { useAutoHideMessage } from '@/hooks/useAutoHideMessage';
import { useIsMounted } from '@/hooks/useIsMounted';
import OrderSectionHeader from './OrderSectionHeader';
import OrderToolbar from './OrderToolbar';
import TableWrapper from './TableWrapper';
import { formatSummaryAmount } from '@/utils/formatters';
import { SCHEME_REGION_DOMESTIC, SCHEME_REGION_GLOBAL } from '@/config/constants';
import styles from './OrderManagement.module.css';
import shared from '@/components/utils/shared.module.css';
import { Button, Note, Placeholder } from '@/components/ui';

/** Minimal shape every in-progress order must satisfy. */
export interface BaseInProgressOrder {
  id: number;
  scheme: { region?: number | null } | null;
}

const regionOf = (order: BaseInProgressOrder) => order.scheme?.region ?? SCHEME_REGION_DOMESTIC;

type Side = 'buy' | 'sell';

interface ProcessedOrder {
  scheme_id?: number;
  unfunded_amount?: string;
  unfunded_client_count?: number;
  currency?: string;
}

interface ProcessResult {
  errors?: { order_id: number; error: string }[];
  processed_orders?: ProcessedOrder[];
  processed_count?: number;
}

// Each order's figure supersedes the previous one for the same scheme, so the last
// entry per scheme is what is actually still outstanding.
const outstandingFunding = (processed: ProcessedOrder[]): string | null => {
  const latestByScheme = new Map<number, ProcessedOrder>();
  processed.forEach(order => {
    if (order.unfunded_client_count !== undefined) latestByScheme.set(order.scheme_id ?? 0, order);
  });

  const short = [...latestByScheme.values()].filter(o => (o.unfunded_client_count ?? 0) > 0);
  if (short.length === 0) return null;

  return short
    .map(o => `${formatSummaryAmount(o.unfunded_amount, o.currency)} across ${o.unfunded_client_count} client(s)`)
    .join('; ');
};

export interface BaseInProgressOrdersProps<
  TBuy extends BaseInProgressOrder,
  TSell extends BaseInProgressOrder,
> {
  /** Prefix shown in section headers, e.g. "ETF" or "" */
  titlePrefix?: string;
  /** When true, renders both "Process Block" and "Process Selected" buttons */
  supportsBlockTrade?: boolean;
  fetchBuyOrders: () => Promise<{ orders: TBuy[] }>;
  fetchSellOrders: () => Promise<{ orders: TSell[] }>;
  processBuyOrders: (payload: { order_ids: number[]; block_trade?: boolean }) => Promise<ProcessResult>;
  processSellOrders: (payload: { order_ids: number[]; block_trade?: boolean }) => Promise<ProcessResult>;
  onProcessSuccess: (blockTrade: boolean, remaining: number) => void;
  /** Render the buy-specific <th> cells */
  renderBuyHeaders: () => React.ReactNode;
  /** Render the sell-specific <th> cells */
  renderSellHeaders: () => React.ReactNode;
  /** Render buy-specific <td> cells for a single row */
  renderBuyRow: (order: TBuy) => React.ReactNode;
  /** Render sell-specific <td> cells for a single row */
  renderSellRow: (order: TSell) => React.ReactNode;
  /** Shared leading <th> cells (checkbox, Order Date, …) */
  renderSharedHeaders: () => React.ReactNode;
  /** Shared leading <td> cells for a buy row */
  renderSharedBuyRowCells: (order: TBuy) => React.ReactNode;
  /** Shared leading <td> cells for a sell row */
  renderSharedSellRowCells: (order: TSell) => React.ReactNode;
  /** Shared trailing <th> cells (POV CAP, Vol Cap, Created, …) */
  renderTrailingHeaders?: () => React.ReactNode;
  /** Shared trailing <td> cells for buy */
  renderTrailingBuyRowCells?: (order: TBuy) => React.ReactNode;
  /** Shared trailing <td> cells for sell */
  renderTrailingSellRowCells?: (order: TSell) => React.ReactNode;
}

function BaseInProgressOrders<
  TBuy extends BaseInProgressOrder,
  TSell extends BaseInProgressOrder,
>({
  titlePrefix = '',
  supportsBlockTrade = false,
  fetchBuyOrders,
  fetchSellOrders,
  processBuyOrders,
  processSellOrders,
  onProcessSuccess,
  renderBuyHeaders,
  renderSellHeaders,
  renderBuyRow,
  renderSellRow,
  renderSharedHeaders,
  renderSharedBuyRowCells,
  renderSharedSellRowCells,
  renderTrailingHeaders,
  renderTrailingBuyRowCells,
  renderTrailingSellRowCells,
}: BaseInProgressOrdersProps<TBuy, TSell>): React.ReactElement {
  const [buyOrders, setBuyOrders] = useState<TBuy[]>([]);
  const [sellOrders, setSellOrders] = useState<TSell[]>([]);
  const { selectedBuy, selectedSell, toggleAll, toggle, clearSide } = useOrderSelection();
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const isMounted = useIsMounted();

  useAutoHideMessage(successMessage, () => setSuccessMessage(null));

  const prefix = titlePrefix ? `${titlePrefix} ` : '';

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const [buyRes, sellRes] = await Promise.all([fetchBuyOrders(), fetchSellOrders()]);
      if (!isMounted.current) return null;
      setBuyOrders(buyRes.orders);
      setSellOrders(sellRes.orders);
      return { buy: buyRes.orders, sell: sellRes.orders };
    } catch {
      if (isMounted.current) setError(`Failed to load ${prefix}in-progress orders`);
      return null;
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => { loadOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProcess = async (side: Side, blockTrade = false) => {
    const selected = side === 'buy' ? selectedBuy : selectedSell;
    if (selected.size === 0) { setError('Please select at least one order to process'); return; }
    try {
      setProcessingAction(side);
      setError(null);
      setSuccessMessage(null);
      const fn = side === 'buy' ? processBuyOrders : processSellOrders;
      const result = await fn({ order_ids: Array.from(selected), block_trade: blockTrade });
      if (result.errors && result.errors.length > 0) {
        setError(`Some orders failed: ${result.errors.map(e => `Order ${e.order_id}: ${e.error}`).join(', ')}`);
      }
      const processed = result.processed_orders ?? [];
      if (processed.length > 0 || (result.processed_count ?? 0) > 0) {
        const outstanding = outstandingFunding(processed);
        setSuccessMessage(
          `Processed ${result.processed_count ?? selected.size} ${prefix}${side} order(s)`
          + (outstanding ? ` — still to raise: ${outstanding}` : ''),
        );
        // Both lists reload, so ticks on the other side could point at orders no longer listed.
        clearSide('buy');
        clearSide('sell');
        const reloaded = await loadOrders();
        // The count drives whether the caller may navigate away: leaving the tab while
        // instruments are still unprocessed hides them and drops the message above.
        onProcessSuccess(blockTrade, (side === 'buy' ? reloaded?.buy : reloaded?.sell)?.length ?? 0);
      }
    } catch (err: unknown) {
      const axiosMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      const plainMsg = err && typeof err === 'object' && 'error' in err ? String((err as { error: unknown }).error) : null;
      setError(axiosMsg ?? plainMsg ?? `Failed to process ${prefix}${side} orders`);
    } finally {
      setProcessingAction(null);
    }
  };

  const renderSection = (side: Side) => {
    const orders = side === 'buy' ? (buyOrders as BaseInProgressOrder[]) : (sellOrders as BaseInProgressOrder[]);
    const selected = side === 'buy' ? selectedBuy : selectedSell;
    const label = side === 'buy' ? 'Buy' : 'Sell';
    const isProcessing = processingAction === side;
    // One calculate run is one region (the backend rejects a mix): the first ticked order
    // sets it, and select-all takes the first row's region when nothing is ticked yet.
    const firstSelected = orders.find(o => selected.has(o.id));
    const lockedRegion = firstSelected ? regionOf(firstSelected) : null;
    const activeRegion = lockedRegion ?? (orders.length > 0 ? regionOf(orders[0]) : null);
    const selectable = orders.filter(o => regionOf(o) === activeRegion);
    const hasLockedOutOrders = lockedRegion !== null && selectable.length < orders.length;

    return (
      <div className={styles['order-section']}>
        <OrderSectionHeader
          side={side}
          title={`In-Progress ${prefix}${label} Orders`}
          right={<span className={styles['toolbar-info']}>{orders.length} order{orders.length !== 1 ? 's' : ''}</span>}
        />

        {orders.length === 0 ? (
          <div className={styles.empty}>
            <Placeholder message={<>No in-progress {prefix.toLowerCase()}{label.toLowerCase()} orders found.</>} />
          </div>
        ) : (
          <>
            <OrderToolbar
              selectedCount={selected.size}
              totalCount={orders.length}
              actions={
                <>
                  {supportsBlockTrade && (
                    <Button
                      size="sm"
                      onClick={() => handleProcess(side, true)}
                      disabled={processingAction !== null || selected.size === 0}
                    >
                      {isProcessing ? 'Processing...' : 'Process Block'}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleProcess(side, false)}
                    disabled={processingAction !== null || selected.size === 0}
                  >
                    {isProcessing ? 'Processing…' : `Process Selected (${selected.size})`}
                  </Button>
                </>
              }
            />

            {hasLockedOutOrders && (
              <Note tone="info">
                {lockedRegion === SCHEME_REGION_GLOBAL
                  ? 'Global orders selected — domestic orders can be processed once these are calculated and submitted.'
                  : 'Domestic orders selected — global orders can be processed once these are calculated and submitted.'}
              </Note>
            )}

            <TableWrapper>
              <table className={`${shared.table} ${styles.table}`}>
                <thead>
                  <tr>
                    <th className={styles['col-check']}>
                      <input
                        type="checkbox"
                        checked={selectable.length > 0 && selected.size === selectable.length}
                        onChange={() => toggleAll(side, selectable)}
                      />
                    </th>
                    {renderSharedHeaders()}
                    {side === 'buy' ? renderBuyHeaders() : renderSellHeaders()}
                    {renderTrailingHeaders?.()}
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const isBuy = side === 'buy';
                    const isRegionLocked = lockedRegion !== null && regionOf(order) !== lockedRegion;
                    return (
                      <tr key={order.id}>
                        <td className={styles['col-check']}>
                          <input
                            type="checkbox"
                            checked={selected.has(order.id)}
                            disabled={isRegionLocked}
                            onChange={() => toggle(order.id, side)}
                          />
                        </td>
                        {isBuy
                          ? renderSharedBuyRowCells(order as TBuy)
                          : renderSharedSellRowCells(order as TSell)}
                        {isBuy
                          ? renderBuyRow(order as TBuy)
                          : renderSellRow(order as TSell)}
                        {isBuy
                          ? renderTrailingBuyRowCells?.(order as TBuy)
                          : renderTrailingSellRowCells?.(order as TSell)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrapper>
          </>
        )}
      </div>
    );
  };

  if (loading) return <Placeholder kind="loading" />;

  return (
    <div>
      {(successMessage || error) && (
        <div className={styles.messages}>
          {successMessage && <Note tone="success">{successMessage}</Note>}
          {error && <Note tone="error">{error}</Note>}
        </div>
      )}
      {renderSection('buy')}
      {renderSection('sell')}
    </div>
  );
}

export default BaseInProgressOrders;
